import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useUserData } from '@nhost/react'
import { notifyError } from '@/shared/lib/notify'
import {
  PAID_STORAGE_KEY,
  VOUCHER_CODE_KEY,
  VOUCHER_DISCOUNT_KEY,
  readStorage,
  removeStorage,
  writeStorage,
} from '@/shared/lib/storageKeys'
import { GET_USER_PAID_STATUS, SET_USER_PAID } from './api'

/**
 * Der Bezahlstatus für die Leseansichten. `null` heißt "noch nicht entschieden" - erst
 * wenn die Datenbank geantwortet hat, steht `true` oder `false` fest. localStorage ist
 * nur der Zwischenspeicher für den ersten Render, damit zahlende Nutzerinnen nicht für
 * einen Moment die Schranke sehen; die Datenbank hat immer das letzte Wort.
 *
 * Kehrt Stripe mit `?payment=success` zurück, gilt der Zugang sofort als bezahlt und
 * wird im Hintergrund gespeichert - sonst läge die Schranke noch über dem Text, während
 * die Zahlung längst durch ist.
 */
export function usePaidState(): boolean | null {
  const user = useUserData()

  const [isPaid, setIsPaid] = useState<boolean | null>(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('payment') === 'success') {
      writeStorage(PAID_STORAGE_KEY, 'true')
      return true
    }
    return readStorage(PAID_STORAGE_KEY) === 'true' ? true : null
  })

  const {
    data: paidData,
    loading: paidLoading,
    error: paidError,
  } = useQuery(GET_USER_PAID_STATUS, {
    variables: { userId: user?.id ?? '' },
    skip: !user?.id,
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (!user?.id || paidLoading) return
    if (paidError) {
      console.error('Failed to load paid status:', paidError)
      notifyError(paidError, 'Der Bezahlstatus konnte nicht geladen werden')
      setIsPaid(readStorage(PAID_STORAGE_KEY) === 'true')
      return
    }
    if (!paidData) return

    const paidStatus = paidData.user_profiles?.[0]?.paid === true
    setIsPaid(paidStatus)

    if (paidStatus) writeStorage(PAID_STORAGE_KEY, 'true')
    else removeStorage(PAID_STORAGE_KEY)
  }, [user?.id, paidLoading, paidError, paidData])

  const [setUserPaidMutation] = useMutation(SET_USER_PAID)
  const savePaidStatusToDb = useCallback(async () => {
    if (!user?.id) return
    try {
      await setUserPaidMutation({ variables: { userId: user.id } })
    } catch (error) {
      console.error('Failed to save paid status:', error)
      notifyError(
        error,
        'Die Zahlung ist angekommen, aber der Bezahlstatus konnte nicht gespeichert werden'
      )
    }
  }, [user?.id, setUserPaidMutation])

  // Rückkehr von Stripe: speichern, Gutschein aufräumen, Parameter aus der Adresse nehmen.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('payment') !== 'success') return

    savePaidStatusToDb()
    removeStorage(VOUCHER_CODE_KEY)
    removeStorage(VOUCHER_DISCOUNT_KEY)

    params.delete('payment')
    const newSearch = params.toString()
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (newSearch ? '?' + newSearch : '')
    )
  }, [savePaidStatusToDb])

  return isPaid
}
